/* Daily vocal warm-up routine; completed steps are remembered per day. */
import { type ReactNode, useState } from 'react';
import { useGo } from '../../app/navigation';
import { audio, type Player } from '../../core/audio';
import { addXp, award, getRecord, setRecord } from '../../core/store';
import { toast } from '../../core/toast';
import { dateKey } from '../../core/util';
import { Btn, PlayButton } from '../../ui/Button';
import { Icon } from '../../ui/icons';
import { modStyle } from '../../ui/style';
import { BreathWidget } from '../lesson/BreathWidget';
import { rangeText, tess, vinst } from './voice';

interface Step {
  title: string;
  text: string;
  widget?: ReactNode;
  play?: () => Player;
  route?: string;
}

export function Warmup() {
  const go = useGo();
  const key = `isinma.${dateKey()}`;
  const [done, setDone] = useState(() => new Set(getRecord<number[]>(key) || []));
  const t = tess();
  const mid = Math.round((t.lo + t.hi) / 2);
  const steps: Step[] = [
    {
      title: 'Nefes',
      text: 'Diyafram nefesi: 4 saniye al, 4 tut, 8 saniyede "sss" diye ver. Üç tur.',
      widget: (
        <div style={{ marginTop: '10px' }}>
          <div className="lesson-body">
            <BreathWidget pattern={[4, 4, 8]} rounds={3} />
          </div>
        </div>
      ),
    },
    {
      title: 'Dudak titreşimi',
      text: 'Dudaklarını gevşek bırak, "brrr" diye titreştirirken kılavuz sesle birlikte aşağı yukarı kay.',
      play: () =>
        audio.playGlide(
          [
            [0, mid - 5],
            [1.6, mid + 4],
            [3.2, mid - 5],
            [4.8, mid + 6],
            [6.4, mid - 5],
          ],
          { vel: 0.18 },
        ),
    },
    {
      title: "Mırıldanarak 5'li gam",
      text: 'Ağzın kapalı "mmm" ile, rahat bir tonda. Titreşimi dudaklarında ve burnunda hisset.',
      route: 'vokal.egz.gam.1',
    },
    {
      title: '"Ma-me-mi-mo-mu" arpejleri',
      text: 'Ünlüleri açık ve net söyle; çeneni zorlamadan bırak.',
      route: 'vokal.egz.gam.3',
    },
    { title: 'Siren', text: 'Çizgiyi takip ederek geçiş bölgende sesini yumuşat.', route: 'vokal.egz.siren.1' },
    {
      title: 'Soğuma',
      text: 'Rahat bir tonda "uu" ile inen 5\'li gam. Sesi küçült, nefesi yavaşlat.',
      play: () => audio.playNotes([mid + 7, mid + 5, mid + 4, mid + 2, mid], { bpm: 70, inst: vinst() }),
    },
  ];

  const commit = (next: Set<number>) => {
    setDone(next);
    setRecord(key, Array.from(next));
  };
  const toggle = (i: number) => {
    const next = new Set(done);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    commit(next);
    if (next.size === steps.length && !getRecord(`${key}.xp`)) {
      setRecord(`${key}.xp`, 1);
      addXp(20, 'vokal');
      award('isinma');
      toast('Isınma tamam! +20 XP', { tone: 'good', icon: 'check' });
    }
  };

  return (
    <div className="ex-page" style={modStyle('vokal')}>
      <button type="button" className="link-back" onClick={() => go('vokal')}>
        <Icon name="left" />
        <span>Vokal &amp; Şan</span>
      </button>
      <div className="ex-start-card">
        <div className="eyebrow">Rutin · yaklaşık 8 dakika</div>
        <h1 className="ex-start-title">Günlük ısınma</h1>
        <p className="ex-start-desc">
          Şarkı söylemeden önce sesini hazırla. Adımları sırayla yap; hepsini bitirince günün ısınması tamamlanır.
        </p>
        <p className="mb-note">Ses aralığın: {rangeText()}</p>
      </div>
      <div className="routine">
        {steps.map((s, i) => {
          const isDone = done.has(i);
          return (
            <div key={s.title} className={`rt-step${isDone ? ' done' : ''}`}>
              <span className="rt-num">{isDone ? <Icon name="check" /> : String(i + 1)}</span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
                {s.widget}
              </div>
              <div className="sc-actions">
                {s.route ? (
                  <Btn
                    kind="soft"
                    icon="play"
                    onClick={() => {
                      commit(new Set(done).add(i));
                      go(s.route as string);
                    }}
                  >
                    Başlat
                  </Btn>
                ) : null}
                {s.play ? <PlayButton label="Dinle" start={s.play} /> : null}
                <Btn kind={isDone ? 'ghost' : 'primary'} icon="check" onClick={() => toggle(i)}>
                  {isDone ? 'Yapıldı' : 'Tamam'}
                </Btn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
